import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { CssFromFunction } from '@upright/ui/common/generic/types/types';
import { throttle } from 'lodash';
import { ReactNode, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { TableAreaStyles, TableV2Variant } from '../../types';

/**
 * Handles TableV2's horizontal scroll as well as the right edge fade for overflown content
 */

export const TableAreaWrap = (props: {
  children: ReactNode;
  styleOverrides: TableAreaStyles | undefined;
  overflowFadeDeps: unknown[];
  variant: TableV2Variant;
}) => {
  const [overflownContentWidth, setOverflownContentWidth] = useState(0);

  const backgroundRef = useRef<HTMLDivElement>(null);
  const foregroundRef = useRef<HTMLDivElement>(null);
  const backgroundElement = backgroundRef.current;
  const foregroundElement = foregroundRef.current;

  // Update overflownContentWidth once in the beginning, and at any time when foregroundElement and backgroundElement update. Also gets called when the
  // overflowFadeDepsdeps prop changes so that we can force the fade to appear on demand from the calling context.
  useEffect(() => {
    setOverflownContentWidth(getOverflownContentWidth(foregroundElement, backgroundElement));

    // We break the rules of hooks here with two ways:
    // 1) The scrollWidth dependency is not used within the hook, but is listed in the dependency array.
    // Reason: Tables that have dynamic column widths can change width when the content arrives. The scrollWidth dependency forces a rerender when
    // that happens (though it requires that something else first rerenders this component to trigger the change in this useEffect).
    //
    // 2) Linter will warn about the spread array deps. But we want those so that the calling context can force a refresh flexibly.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foregroundElement, backgroundElement, foregroundElement?.scrollWidth, ...props.overflowFadeDeps]);

  // Recompute when the table or its frame changes size without a window resize or a re-render of this
  // component: rows arriving after the loading state, a sticky side panel opening beside the table,
  // fonts loading. Without this the fade measured on the loading skeleton stayed painted over a table
  // that no longer overflows, hiding its last column (seen on the Arvo console's Usage tables).
  useEffect(() => {
    if (!foregroundElement || !backgroundElement || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      setOverflownContentWidth(getOverflownContentWidth(foregroundElement, backgroundElement));
    });
    observer.observe(foregroundElement);
    observer.observe(backgroundElement);
    if (foregroundElement.firstElementChild) observer.observe(foregroundElement.firstElementChild);
    return () => {
      observer.disconnect();
    };
  }, [foregroundElement, backgroundElement]);

  // Adds event listeners to table scroll and window resize so that the overflownContentWidth gets updated when the events are fired.
  useEffect(() => {
    function getAndSetOverflow() {
      setOverflownContentWidth(getOverflownContentWidth(foregroundElement, backgroundElement));
    }

    if (!foregroundElement) return;
    // The event listeners are throttled to trigger new rerenders only every 50ms to avoid performance issues.
    foregroundElement.addEventListener('scroll', throttle(getAndSetOverflow, 50));
    window.addEventListener('resize', throttle(getAndSetOverflow, 50));

    return () => {
      foregroundElement.removeEventListener('scroll', getAndSetOverflow);
      window.removeEventListener('resize', getAndSetOverflow);
    };
  }, [foregroundElement, backgroundElement]);

  return (
    <WrapBackground ref={backgroundRef} styleOverride={props.styleOverrides?.back}>
      <WrapForeground
        ref={foregroundRef}
        styleOverride={props.styleOverrides?.front}
        overflownContentWidthPx={overflownContentWidth}
        containerVisibleWidthPx={foregroundElement?.clientWidth || 0}
        $variant={props.variant}
      >
        {props.children}
      </WrapForeground>
    </WrapBackground>
  );
};

// The percentage of width used for the right edge overflow linear fade
const fadeWidthPct = 15;

function getOverflownContentWidth(foregroundElement: HTMLDivElement | null, backgroundElement: HTMLDivElement | null) {
  if (!foregroundElement || !backgroundElement) return 0;
  const overflownContentWidth =
    foregroundElement.scrollWidth - backgroundElement.scrollWidth - foregroundElement.scrollLeft;
  return Math.max(overflownContentWidth, 0);
}

const WrapBackground = styled.div<{ styleOverride: CssFromFunction | undefined }>`
  ${props => props.styleOverride}
`;

const WrapForeground = styled.div<{
  styleOverride: CssFromFunction | undefined;
  overflownContentWidthPx: number;
  containerVisibleWidthPx: number;
  $variant: TableV2Variant;
}>`
  max-height: max-content;
  // Make table x-scrollable when it gets too small
  overflow-x: auto;
  border: 1px solid ${BouColor.PLATFORM_BORDER_GRAY_LIGHTER};
  border-radius: ${props => (props.$variant === 'plain' ? 'var(--border-radius__lg)' : 'var(--border-radius__sm)')};
  ${props =>
    props.overflownContentWidthPx > 0 &&
    css`
      /* We "paint" a mask over the content to achieve the right edge fade. When the mask is black, the child content (the table) shows.
      When the mask is transparent, the child content won't show, but anything "behind" the component will show through, i.e. this
      fade works on any background color. When it's anything in-between, the child content and the stuff behind are mixed, with the proportions
      depending on the "blackness" of the mask */
      mask-image: linear-gradient(
        to left,
        rgba(0, 0, 0, ${getFadeOpacity(props.overflownContentWidthPx, props.containerVisibleWidthPx)}) 0%,
        rgba(0, 0, 0, 1) ${fadeWidthPct}%
      );
    `}
  ${props => props.styleOverride}
`;

/**
 * This function determines how much we should fade the right edge of the container.
 *
 * As long as the content that overflows to the right is wider than the fade width, the fade goes all the way to
 * fully transparent (i.e. opacity 0 for the mask).
 *
 * When the overflowing content is narrower than the fade width, we gradually bring the opacity to 1, based on
 * the share of content overflown. This enables a nice and smooth easing out of the fade.
 *
 * @param overflownContentWidthPx The pixel width of content that is still to the right of the container
 * @param containerVisibleWidthPx The pixel width of the container that is visible on the screen
 * @returns Opacity value between 0-1.
 */
function getFadeOpacity(overflownContentWidthPx: number, containerVisibleWidthPx: number) {
  const fadeWidthPx = (fadeWidthPct / 100) * containerVisibleWidthPx;
  const overflownContentPerFadeWidth = overflownContentWidthPx / fadeWidthPx;
  const maskOpacity = overflownContentPerFadeWidth * -1 + 1;
  /* The table can in theory be inifinitely wide, so maskOpacity has a range of [-∞, 1].
     When it's below zero, i.e. when the content overflowing to the right is wider than the fade, let's just return zero. */
  return Math.max(maskOpacity, 0);
}
